import React from "react";
import { MotionBox } from "@rusha/shared-theme";
import { hubInitial, hubInView, hubTransition } from "./HeroAnimations";

const HeroHub: React.FC = () => {
  return (
    <MotionBox
      aria-hidden="true"
      initial={hubInitial}
      whileInView={hubInView}
      viewport={{ once: true }}
      transition={hubTransition}
      sx={{
        width: "100%",
        maxWidth: { md: 620, lg: 680 },
        mx: "auto",
        display: { xs: "none", md: "block" },
      }}
    >
      <svg
        viewBox="440 90 500 330"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <filter
            id="glowCyanHero"
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
          >
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter
            id="softGlowHero"
            x="-30%"
            y="-30%"
            width="160%"
            height="160%"
          >
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="ringGradHero" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#00d4ff" />
            <stop offset="50%" stopColor="#7b61ff" />
            <stop offset="100%" stopColor="#00d4ff" />
          </linearGradient>
          <linearGradient
            id="hubMarkGradHero"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#00d4ff" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
          <mask id="hubMarkMaskHero">
            <rect x="0" y="0" width="120" height="120" fill="white" />
            <path
              d="M 32 28 L 75 28 C 88 28 93 38 93 48 C 93 56 86 62 75 62 L 32 62 Z"
              fill="black"
            />
            <polygon points="58,65 98,102 88,108 52,72" fill="black" />
          </mask>
        </defs>

        <style>
          {`
            .hero-sat { animation: heroTwinkle 4.2s ease-in-out infinite; }
            .hero-sat-infra { transform-origin: 565px 133px; animation-delay: 0s; }
            .hero-sat-gitops { transform-origin: 815px 133px; animation-delay: 0.7s; }
            .hero-sat-finops { transform-origin: 825px 308px; animation-delay: 1.4s; }
            .hero-sat-obs { transform-origin: 835px 220px; animation-delay: 2.1s; }
            .hero-sat-aiops { transform-origin: 565px 308px; animation-delay: 2.8s; }
            .hero-sat-tmpl { transform-origin: 545px 220px; animation-delay: 3.5s; }
            .hero-sat-devsecops { transform-origin: 690px 383px; animation-delay: 4.2s; }
            @keyframes heroTwinkle {
              0%, 100% { opacity: 0.55; transform: scale(1); }
              50% { opacity: 0.8; transform: scale(1.06); }
            }
          `}
        </style>

        <circle
          cx="690"
          cy="220"
          r="98"
          fill="none"
          stroke="url(#ringGradHero)"
          strokeWidth="0.8"
          strokeOpacity="0.3"
          filter="url(#glowCyanHero)"
        >
          <animate
            attributeName="r"
            values="98;106;98"
            dur="4s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="stroke-opacity"
            values="0.3;0.18;0.3"
            dur="4s"
            repeatCount="indefinite"
          />
        </circle>
        <circle
          cx="690"
          cy="220"
          r="92"
          fill="none"
          stroke="#00d4ff"
          strokeWidth="0.3"
          strokeOpacity="0.15"
          strokeDasharray="4 6"
        >
          <animate
            attributeName="r"
            values="92;100;92"
            dur="4s"
            repeatCount="indefinite"
          />
        </circle>
        <circle
          cx="690"
          cy="220"
          r="58"
          fill="none"
          stroke="url(#ringGradHero)"
          strokeWidth="1.5"
          strokeOpacity="0.4"
          filter="url(#glowCyanHero)"
        >
          <animate
            attributeName="r"
            values="58;64;58"
            dur="4s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="stroke-opacity"
            values="0.4;0.25;0.4"
            dur="4s"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="690" cy="220" r="52" fill="#070d1a" fillOpacity="0.6">
          <animate
            attributeName="r"
            values="52;57;52"
            dur="4s"
            repeatCount="indefinite"
          />
        </circle>

        <g transform="translate(662, 192) scale(0.47)">
          <g mask="url(#hubMarkMaskHero)">
            <path
              d="M 22 18 L 22 102"
              stroke="url(#hubMarkGradHero)"
              strokeWidth="12"
              strokeLinecap="round"
              fill="none"
            />
            <path
              d="M 22 18 L 78 18 C 98 18 105 35 105 48 C 105 62 95 72 78 72 L 22 72"
              stroke="url(#hubMarkGradHero)"
              strokeWidth="12"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
            <path
              d="M 58 65 L 98 102"
              stroke="url(#hubMarkGradHero)"
              strokeWidth="12"
              strokeLinecap="round"
              fill="none"
            />
          </g>
          <circle cx="60" cy="60" r="6" fill="#00d4ff" />
          <circle cx="60" cy="60" r="2.5" fill="#ffffff" />
        </g>
        <text
          x="690"
          y="252"
          fontFamily="Inter, -apple-system, sans-serif"
          fontSize="11"
          fontWeight="800"
          fill="#ffffff"
          textAnchor="middle"
          letterSpacing="-0.02em"
        >
          rusha
        </text>
        <text
          x="690"
          y="264"
          fontFamily="Inter, -apple-system, sans-serif"
          fontSize="7"
          fill="#94a3b8"
          textAnchor="middle"
          opacity="0.5"
        >
          Control Plane
        </text>

        <g className="hero-sat hero-sat-infra">
          <rect
            x="490"
            y="115"
            width="150"
            height="36"
            rx="8"
            fill="#0f1d35"
            fillOpacity="0.5"
            stroke="#00d4ff"
            strokeWidth="0.7"
            strokeOpacity="0.4"
          />
          <rect
            x="500"
            y="123"
            width="20"
            height="20"
            rx="5"
            fill="#00d4ff"
            fillOpacity="0.08"
          />
          <path
            d="M505 129 L510 129 L510 133 L514 133 L514 137 L510 137 L510 133 L505 133 Z"
            fill="#00d4ff"
            opacity="0.7"
          />
          <text
            x="526"
            y="135"
            fontFamily="Inter, -apple-system, sans-serif"
            fontSize="10.5"
            fontWeight="600"
            fill="#e2e8f0"
            opacity="0.75"
          >
            Infrastructure
          </text>
          <text
            x="526"
            y="145"
            fontFamily="Inter, -apple-system, sans-serif"
            fontSize="7.5"
            fill="#94a3b8"
            opacity="0.6"
          >
            Provision &amp; scale any infra
          </text>
        </g>

        <g className="hero-sat hero-sat-tmpl">
          <rect
            x="470"
            y="202"
            width="150"
            height="36"
            rx="8"
            fill="#0f1d35"
            fillOpacity="0.5"
            stroke="#a855f7"
            strokeWidth="0.7"
            strokeOpacity="0.4"
          />
          <rect
            x="480"
            y="210"
            width="20"
            height="20"
            rx="5"
            fill="#a855f7"
            fillOpacity="0.08"
          />
          <rect
            x="485"
            y="215"
            width="10"
            height="10"
            rx="2"
            fill="none"
            stroke="#a855f7"
            strokeWidth="1"
            opacity="0.7"
          />
          <rect
            x="487"
            y="217"
            width="6"
            height="6"
            rx="1"
            fill="#a855f7"
            opacity="0.7"
          />
          <text
            x="506"
            y="222"
            fontFamily="Inter, -apple-system, sans-serif"
            fontSize="10.5"
            fontWeight="600"
            fill="#e2e8f0"
            opacity="0.75"
          >
            Templates
          </text>
          <text
            x="506"
            y="232"
            fontFamily="Inter, -apple-system, sans-serif"
            fontSize="7.5"
            fill="#94a3b8"
            opacity="0.6"
          >
            Start from proven templates
          </text>
        </g>

        <g className="hero-sat hero-sat-aiops">
          <rect
            x="490"
            y="290"
            width="150"
            height="36"
            rx="8"
            fill="#0f1d35"
            fillOpacity="0.5"
            stroke="#ec4899"
            strokeWidth="0.7"
            strokeOpacity="0.4"
          />
          <rect
            x="500"
            y="298"
            width="20"
            height="20"
            rx="5"
            fill="#ec4899"
            fillOpacity="0.08"
          />
          <circle
            cx="510"
            cy="308"
            r="3.5"
            fill="none"
            stroke="#ec4899"
            strokeWidth="1"
            opacity="0.7"
          />
          <circle cx="510" cy="308" r="1.2" fill="#ec4899" opacity="0.7" />
          <path
            d="M510 302 L510 304 M510 312 L510 314 M504 308 L506 308 M514 308 L516 308"
            stroke="#ec4899"
            strokeWidth="0.8"
            opacity="0.7"
          />
          <text
            x="526"
            y="310"
            fontFamily="Inter, -apple-system, sans-serif"
            fontSize="10.5"
            fontWeight="600"
            fill="#e2e8f0"
            opacity="0.75"
          >
            AI Ops
          </text>
          <text
            x="526"
            y="320"
            fontFamily="Inter, -apple-system, sans-serif"
            fontSize="7.5"
            fill="#94a3b8"
            opacity="0.6"
          >
            Smart incident response
          </text>
        </g>

        <g className="hero-sat hero-sat-gitops">
          <rect
            x="740"
            y="115"
            width="150"
            height="36"
            rx="8"
            fill="#0f1d35"
            fillOpacity="0.5"
            stroke="#22c55e"
            strokeWidth="0.7"
            strokeOpacity="0.4"
          />
          <rect
            x="750"
            y="123"
            width="20"
            height="20"
            rx="5"
            fill="#22c55e"
            fillOpacity="0.08"
          />
          <text
            x="753"
            y="137"
            fontFamily="monospace"
            fontSize="9"
            fontWeight="600"
            fill="#22c55e"
            opacity="0.7"
          >
            {"</>"}
          </text>
          <text
            x="776"
            y="135"
            fontFamily="Inter, -apple-system, sans-serif"
            fontSize="10.5"
            fontWeight="600"
            fill="#e2e8f0"
            opacity="0.75"
          >
            GitOps
          </text>
          <text
            x="776"
            y="145"
            fontFamily="Inter, -apple-system, sans-serif"
            fontSize="7.5"
            fill="#94a3b8"
            opacity="0.6"
          >
            Git-driven deployments
          </text>
        </g>

        <g className="hero-sat hero-sat-obs">
          <rect
            x="760"
            y="202"
            width="150"
            height="36"
            rx="8"
            fill="#0f1d35"
            fillOpacity="0.5"
            stroke="#22d3ee"
            strokeWidth="0.7"
            strokeOpacity="0.4"
          />
          <rect
            x="770"
            y="210"
            width="20"
            height="20"
            rx="5"
            fill="#22d3ee"
            fillOpacity="0.08"
          />
          <rect
            x="775"
            y="215"
            width="2.5"
            height="10"
            rx="0.8"
            fill="#22d3ee"
            opacity="0.7"
          />
          <rect
            x="779"
            y="212"
            width="2.5"
            height="13"
            rx="0.8"
            fill="#22d3ee"
            opacity="0.7"
          />
          <rect
            x="783"
            y="217"
            width="2.5"
            height="8"
            rx="0.8"
            fill="#22d3ee"
            opacity="0.7"
          />
          <text
            x="796"
            y="252"
            fontFamily="Inter, -apple-system, sans-serif"
            fontSize="10.5"
            fontWeight="600"
            fill="#e2e8f0"
            opacity="0.75"
          >
            Observability
          </text>
          <text
            x="796"
            y="262"
            fontFamily="Inter, -apple-system, sans-serif"
            fontSize="7.5"
            fill="#94a3b8"
            opacity="0.6"
          >
            Full-stack visibility
          </text>
        </g>

        <g className="hero-sat hero-sat-finops">
          <rect
            x="750"
            y="290"
            width="150"
            height="36"
            rx="8"
            fill="#0f1d35"
            fillOpacity="0.5"
            stroke="#f59e0b"
            strokeWidth="0.7"
            strokeOpacity="0.4"
          />
          <rect
            x="760"
            y="298"
            width="20"
            height="20"
            rx="5"
            fill="#f59e0b"
            fillOpacity="0.08"
          />
          <text
            x="765"
            y="312"
            fontFamily="Inter, -apple-system, sans-serif"
            fontSize="12"
            fontWeight="700"
            fill="#f59e0b"
            opacity="0.7"
          >
            $
          </text>
          <text
            x="786"
            y="310"
            fontFamily="Inter, -apple-system, sans-serif"
            fontSize="10.5"
            fontWeight="600"
            fill="#e2e8f0"
            opacity="0.75"
          >
            FinOps
          </text>
          <text
            x="786"
            y="320"
            fontFamily="Inter, -apple-system, sans-serif"
            fontSize="7.5"
            fill="#94a3b8"
            opacity="0.6"
          >
            Cost visibility &amp; control
          </text>
        </g>

        <g className="hero-sat hero-sat-devsecops">
          <rect
            x="615"
            y="365"
            width="150"
            height="36"
            rx="8"
            fill="#0f1d35"
            fillOpacity="0.5"
            stroke="#ef4444"
            strokeWidth="0.7"
            strokeOpacity="0.4"
          />
          <rect
            x="625"
            y="373"
            width="20"
            height="20"
            rx="5"
            fill="#ef4444"
            fillOpacity="0.08"
          />
          <path
            d="M635 376 L641 378 L641 384 C641 387 638 390 635 391 C632 390 629 387 629 384 L629 378 Z"
            fill="#ef4444"
            opacity="0.7"
          />
          <path
            d="M632 383 L634 385 L638 381"
            fill="none"
            stroke="#0f1d35"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <text
            x="651"
            y="385"
            fontFamily="Inter, -apple-system, sans-serif"
            fontSize="10.5"
            fontWeight="600"
            fill="#e2e8f0"
            opacity="0.75"
          >
            DevSecOps
          </text>
          <text
            x="651"
            y="395"
            fontFamily="Inter, -apple-system, sans-serif"
            fontSize="7.5"
            fill="#94a3b8"
            opacity="0.6"
          >
            Shift-left security
          </text>
        </g>
      </svg>
    </MotionBox>
  );
};

export default HeroHub;
